import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { displayValue, groupIntoSections, rowSchemaOf, visibleFields } from '@portal/forms';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { getDirectoryUser, listDirectoryUsers, searchDirectoryUsers } from '../api/client';
import { DirectoryUserPicker } from './DirectoryUserPicker';
import { Field } from './Field';
import { ListField } from './ListField';
import { settledPhone } from './settled-phone';

// Re-exported so the tests that pin the blur-time tidying keep their import.
export { settledPhone };

/** The directory as the renderer sees it; the real one by default, a fake in tests. */
const liveDirectory = {
  list: listDirectoryUsers,
  search: searchDirectoryUsers,
  get: getDirectoryUser,
};

/**
 * One of a directory user's current values, as a person should read it beside the box:
 * the option label for a select, a list joined for Graph's list-valued phones, nothing
 * at all when the user has no value for it.
 */
export function formatCurrent(hint, value) {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : undefined;
  return displayValue(hint, value);
}

/**
 * Renders ANY registered form. There is no per-form React code: the definition's
 * `fields` say what to draw and its `schema` says what is valid — the very same Zod
 * object the server validates with. Client-side validation is for the user's
 * convenience; the server's is the one that counts.
 *
 * `onSubmit` receives the *parsed* payload (trimmed, normalised) — the schema's
 * output. `serverErrors` are the field errors the server reported (400
 * validation_failed). `directorySearch` says whether a `directory-user` field may
 * search; `directory` is where it searches (injectable for tests).
 */
export function FormRenderer({
  form,
  onSubmit,
  disabled,
  serverErrors,
  directorySearch = false,
  directory = liveDirectory,
}) {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: standardSchemaResolver(form.schema),
    mode: 'onBlur',
    // Values for fields that are not on screen are dropped, so a form whose fields
    // depend on an earlier answer submits only what it actually asked.
    shouldUnregister: true,
    // A checkbox's value is a boolean, so it starts unticked rather than blank; a list
    // starts empty. A hint may name a starting value (a country select opening on
    // Canada); otherwise blank.
    defaultValues: Object.fromEntries(
      form.fields.map((f) => [
        f.name,
        f.widget === 'list' ? [] : (f.defaultValue ?? (f.widget === 'checkbox' ? false : '')),
      ]),
    ),
  });

  // Watch only the fields something else's visibility depends on. Watching the whole
  // form would re-render every control on every keystroke.
  const controllers = useMemo(
    () => [...new Set(form.fields.flatMap((f) => (f.visibleWhen ? [f.visibleWhen.field] : [])))],
    [form],
  );
  const controllerValues = useWatch({ control, name: controllers });
  const shown = visibleFields(
    form.fields,
    Object.fromEntries(controllers.map((name, index) => [name, controllerValues[index]])),
  );

  // The directory user the submitter picked, and their current attribute values, shown
  // beside the other boxes. Cleared the moment the username box says someone else.
  const [currentValues, setCurrentValues] = useState(undefined);
  const pickedUpn = useRef(undefined);

  // If the server disagreed with the browser (it should not, but it is the
  // authority), surface its messages on the fields.
  useEffect(() => {
    for (const [name, message] of Object.entries(serverErrors ?? {})) {
      setError(name, { type: 'server', message });
    }
  }, [serverErrors, setError]);

  /**
   * Per-widget registration. A phone box is tidied into E.164 as the user leaves it,
   * so the field shows the exact value the schema will store and the submitter is not
   * surprised by the receipt. The schema normalises again on submit, so this is purely
   * for their eyes — the stored value is the same either way.
   */
  function registerField(field) {
    if (field.widget !== 'tel') return register(field.name);
    return register(field.name, {
      onBlur: (event) => {
        const typed = event.target.value;
        const settled = settledPhone(typed);
        if (settled !== typed) {
          setValue(field.name, settled, { shouldDirty: true, shouldValidate: true });
        }
      },
    });
  }

  async function handlePick(user) {
    pickedUpn.current = user.userPrincipalName;
    setCurrentValues(undefined);
    let attributes;
    try {
      attributes = await directory.get(user.userPrincipalName);
    } catch {
      attributes = undefined; // The hints are a convenience; the form works without them.
    }
    // Only if the box still names the user we asked about.
    if (pickedUpn.current === user.userPrincipalName) setCurrentValues(attributes);
  }

  function handleUsernameChange(next) {
    if (next !== pickedUpn.current) {
      pickedUpn.current = undefined;
      setCurrentValues(undefined);
    }
  }

  /** A directory-user field: the picker, driven by react-hook-form's Controller. */
  function renderDirectoryField(field) {
    return (
      <Field
        key={field.name}
        hint={field}
        error={errors[field.name]?.message}
        render={({ id, describedBy, invalid, placeholder }) => (
          <Controller
            name={field.name}
            control={control}
            render={({ field: bound }) => (
              <DirectoryUserPicker
                id={id}
                value={bound.value}
                onChange={(next) => {
                  handleUsernameChange(next);
                  bound.onChange(next);
                }}
                onBlur={bound.onBlur}
                onPick={handlePick}
                directory={directory}
                enabled={directorySearch}
                disabled={disabled || isSubmitting}
                placeholder={placeholder}
                describedBy={describedBy}
                invalid={invalid}
              />
            )}
          />
        )}
      />
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit((values) => onSubmit(values))}>
      {form.description && <p className="muted">{form.description}</p>}

      {groupIntoSections(shown).map((group) => (
        <fieldset key={group.section ?? group.fields[0].name} disabled={disabled || isSubmitting}>
          {group.section && <legend>{group.section}</legend>}
          {group.fields.map((field) =>
            field.widget === 'directory-user' ? (
              renderDirectoryField(field)
            ) : field.widget === 'list' ? (
              <ListField
                key={field.name}
                hint={field}
                control={control}
                register={register}
                setValue={setValue}
                getValues={getValues}
                trigger={trigger}
                errors={errors}
                disabled={disabled || isSubmitting}
                rowSchema={rowSchemaOf(form.schema.shape[field.name])}
              />
            ) : (
              <Field
                key={field.name}
                hint={field}
                registration={registerField(field)}
                error={errors[field.name]?.message}
                current={formatCurrent(field, currentValues?.[field.name])}
              />
            ),
          )}
        </fieldset>
      ))}

      <div className="actions">
        <button type="submit" className="button" disabled={disabled || isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Submit request'}
        </button>
      </div>
    </form>
  );
}
